import { DateFormatter, parseDate, today } from "@internationalized/date";
import { usePostHog } from "@posthog/react";
import { useDateFormatter } from "@react-aria/i18n";
import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { CheckIcon, Shirt } from "lucide-react";
import {
  Activity,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import { match, P } from "ts-pattern";

import { useIsLoggedIn } from "@/auth/shared";
import { Button, button } from "@/components/base/button";
import {
  DropdownMenu,
  DropdownMenuItemLink,
} from "@/components/base/dropdown-menu";
import { Link } from "@/components/base/link";
import { subtitle, title } from "@/components/base/primitives";
import { Tab, TabList, Tabs } from "@/components/base/tabs";
import { tagStyles } from "@/components/base/tag-group";
import { GalleryPanel } from "@/components/gallery/gallery-panel";
import {
  useIsRegistrationOpen,
  useRegistrationSettings,
} from "@/components/registrations/context";
import {
  useCanUpdateTournament,
  useIsDemoTournament,
} from "@/components/tournaments/context";
import { GamesPanel } from "@/components/tournaments/panels/games";
import { InformationPanel } from "@/components/tournaments/panels/information";
import { PlayoffsPanel } from "@/components/tournaments/panels/playoffs";
import { PoolsPanel } from "@/components/tournaments/panels/pools";
import { TeamsPanel } from "@/components/tournaments/panels/teams";
import { TshirtPickupModal } from "@/components/tournaments/tshirt-pickup-modal";
import { VenueHeader } from "@/components/venues/header";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTdNotifications } from "@/hooks/use-td-notifications";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getDefaultTimeZone } from "@/lib/dates";

const TDToolbar = lazy(() => import("@/components/tournaments/td-toolbar"));

const shortDateFormatter = new DateFormatter("EN-US", {
  dateStyle: "short",
  timeZone: getDefaultTimeZone(),
});

const shortDateTimeFormatter = new DateFormatter("EN-US", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: getDefaultTimeZone(),
});

const moneyFormatter = new Intl.NumberFormat("EN-US", {
  style: "currency",
  currency: "usd",
});

export const Route = createFileRoute(
  "/tournaments/$tournamentId/$divisionId/{-$tab}",
)({
  ssr: "data-only",
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    pools?: number[];
    courts?: string[];
  } => {
    return {
      pools: Array.isArray(search?.pools) ? search.pools : undefined,
      courts: Array.isArray(search?.courts) ? search.courts : undefined,
    };
  },
  loader: ({
    params: { tournamentId, divisionId },
    context: { queryClient, trpc },
  }) => {
    const tid = Number.parseInt(tournamentId, 10);
    const did = Number.parseInt(divisionId, 10);

    queryClient.prefetchQuery(trpc.tournaments.get.queryOptions({ id: tid }));
    queryClient.prefetchQuery(
      trpc.tournaments.getDirectors.queryOptions({ tournamentId: tid }),
    );
    queryClient.prefetchQuery(
      trpc.tournaments.getDivisionSummary.queryOptions({
        tournamentDivisionId: did,
      }),
    );
    queryClient.prefetchQuery(
      trpc.gallery.hasPhotos.queryOptions({ tournamentId: tid }),
    );
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          {
            title: [
              shortDateFormatter.format(
                parseDate(loaderData.date).toDate(getDefaultTimeZone()),
              ),
              loaderData.name,
              `${loaderData.venue.name}, ${loaderData.venue.city}`,
            ].join(" "),
          },
        ]
      : undefined,
  }),
});

function RouteComponent() {
  const { tournamentId, divisionId, tab: tabParam } = Route.useParams();

  const isLoggedIn = useIsLoggedIn();
  const posthog = usePostHog();

  const trpc = useTRPC();
  const canUpdate = useCanUpdateTournament();
  const isDemo = useIsDemoTournament();
  useTdNotifications();
  const queryClient = useQueryClient();

  const tQO = trpc.tournaments.get.queryOptions({
    id: Number.parseInt(tournamentId, 10),
  });

  const { data: tournament } = useSuspenseQuery({
    ...tQO,
    staleTime: 30_000,
  });

  const parsedDate = parseDate(tournament.date);

  const activeDivision =
    tournament.tournamentDivisions.find(
      ({ id }) => id.toString() === divisionId,
    ) ?? tournament.tournamentDivisions[0];

  if (!activeDivision) {
    return (
      <DefaultLayout>
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">Division not found.</p>
        </div>
      </DefaultLayout>
    );
  }

  const rosterPublished = activeDivision.rosterPublished;
  const rosterVisible = rosterPublished || canUpdate;

  const navigate = useNavigate();

  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    ...trpc.tournaments.getDivisionSummary.queryOptions({
      tournamentDivisionId: activeDivision.id,
    }),
    staleTime: 30_000,
  });

  const hasTeams = (summary?.teamCount ?? 0) > 0;
  const canShowTeams = (hasTeams || canUpdate) && rosterVisible;
  const hasPools = (summary?.poolCount ?? 0) > 0;
  const canShowPools = (hasPools || canUpdate) && rosterVisible;
  const hasGames = summary?.hasGames ?? false;
  const canShowGames = (hasGames || canUpdate) && rosterVisible;
  const hasPlayoffs = (summary?.playoffCount ?? 0) > 0;
  const canShowPlayoffs = (hasPlayoffs || canUpdate) && rosterVisible;

  const { data: galleryCount } = useQuery({
    ...trpc.gallery.hasPhotos.queryOptions({
      tournamentId: Number.parseInt(tournamentId, 10),
    }),
    staleTime: 60_000,
  });
  const canShowGallery = (galleryCount?.hasPhotos ?? false) || canUpdate;

  const { data: viewerTeamStatus, isLoading: isRegistrationCheckLoading } =
    useQuery({
      ...trpc.tournaments.isViewerRegistered.queryOptions({
        tournamentDivisionId: activeDivision.id,
      }),
      enabled: isLoggedIn,
      staleTime: 60_000,
    });

  const isViewerRegistered =
    viewerTeamStatus === "confirmed" || viewerTeamStatus === "registered";
  const isViewerOnTeam = viewerTeamStatus != null;

  const isTournamentDay = parsedDate.compare(today(getDefaultTimeZone())) === 0;

  const { data: viewerMembership } = useQuery({
    ...trpc.tournaments.getViewerMembership.queryOptions(),
    enabled: isLoggedIn && isTournamentDay && isViewerOnTeam,
  });

  const [tshirtModalOpen, setTshirtModalOpen] = useState(false);

  useEffect(() => {
    if (isSummaryLoading) return;
    const tabVisible: Record<string, boolean> = {
      teams: canShowTeams,
      pools: canShowPools,
      games: canShowGames,
      playoffs: canShowPlayoffs,
      gallery: canShowGallery,
    };
    if (tabParam && tabVisible[tabParam] === false) {
      navigate({
        to: "/tournaments/$tournamentId/$divisionId/{-$tab}",
        params: { tournamentId, divisionId, tab: "info" },
      });
    }
  }, [
    navigate,
    canShowTeams,
    canShowPools,
    canShowGames,
    canShowPlayoffs,
    canShowGallery,
    isSummaryLoading,
    tabParam,
    tournamentId,
    divisionId,
  ]);

  const { venue } = tournament || {};
  // Treat blank/whitespace names as absent — some tournaments store "" instead of NULL.
  const name = tournament?.name?.trim() || null;

  const dateFormatter = useDateFormatter({
    dateStyle: "full",
    timeZone: getDefaultTimeZone(),
  });

  const formattedDate = dateFormatter.format(
    parsedDate.toDate(getDefaultTimeZone()),
  );

  const { venueClassName, dateClassName } = match({
    name,
    venueName: tournament.venue.name,
    venueCity: tournament.venue.city,
  })
    .with(
      {
        name: null,
        venueName: P.select("venueName"),
        venueCity: P.select("venueCity"),
      },
      () => ({
        venueClassName: title(),
        dateClassName: subtitle({ class: "font-bold my-3" }),
      }),
    )
    .with(
      {
        name: P.select("name"),
        venueName: P.select("venueName"),
        venueCity: P.select("venueCity"),
      },
      () => ({
        venueClassName: subtitle({ class: "font-bold my-0" }),
        dateClassName: subtitle({ class: "my-0" }),
      }),
    )
    .exhaustive();

  const [activeTab, setActiveTab] = useState(tabParam || "info");

  useEffect(() => {
    setActiveTab(tabParam || "info");
  }, [tabParam]);

  const onTabChange = useCallback(
    (key: React.Key) => {
      const tab = String(key);
      setActiveTab(tab);
      navigate({
        to: "/tournaments/$tournamentId/$divisionId/{-$tab}",
        params: { tournamentId, divisionId, tab },
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, tournamentId, divisionId],
  );

  const prefetchTab = useCallback(
    (tab: string) => {
      const tid = Number.parseInt(tournamentId, 10);
      const did = activeDivision.id;
      if (tab === "info") {
        queryClient.prefetchQuery(
          trpc.tournaments.getDirectors.queryOptions({ tournamentId: tid }),
        );
      } else if (tab === "teams") {
        queryClient.prefetchQuery(
          trpc.tournaments.getTeams.queryOptions({
            tournamentDivisionId: did,
          }),
        );
      } else if (tab === "pools" || tab === "games") {
        queryClient.prefetchQuery(
          trpc.tournaments.getPools.queryOptions({
            tournamentDivisionId: did,
          }),
        );
      } else if (tab === "playoffs") {
        queryClient.prefetchQuery(
          trpc.tournaments.getPlayoffs.queryOptions({
            tournamentDivisionId: did,
          }),
        );
      } else if (tab === "gallery") {
        queryClient.prefetchQuery(
          trpc.gallery.getPhotos.queryOptions({
            tournamentId: tid,
          }),
        );
      }
    },
    [tournamentId, activeDivision.id, queryClient, trpc],
  );

  const isRegistrationOpen = useIsRegistrationOpen({
    registrationPrice: activeDivision.registrationPrice,
    registrationOpenAt: tournament.registrationOpenAt,
    registrationPaused: activeDivision.registrationPaused,
    divisionName: activeDivision.division.name,
  });

  const { defaultPrice, defaultOpenPrice } = useRegistrationSettings();

  const isOpen = activeDivision.division.name === "open";
  const price =
    activeDivision.registrationPrice ??
    (isOpen ? defaultOpenPrice : defaultPrice);

  const isDivisionFull =
    activeDivision.confirmedCount >= activeDivision.capacity ||
    activeDivision.waitlistedCount > 0;
  const isWaitlistFull =
    activeDivision.confirmedCount + activeDivision.waitlistedCount >=
    activeDivision.capacity + activeDivision.waitlistCapacity;

  return (
    <DefaultLayout classNames={{ content: "bg-white max-w-full" }}>
      <VenueHeader
        {...venue}
        canUpdate={canUpdate}
        onSaved={() => queryClient.invalidateQueries(tQO)}
      />

      <div className="relative">
        {canUpdate && (
          <Suspense>
            <TDToolbar
              tournamentId={Number.parseInt(tournamentId, 10)}
              activeDivision={activeDivision}
              allDivisions={tournament.tournamentDivisions}
              isDemo={isDemo}
            />
          </Suspense>
        )}

        <div className="mx-auto flex max-w-full flex-col space-y-6 py-12 md:max-w-lg">
          <div className="flex flex-col items-center space-y-2 text-center">
            {name && <h1 className={title()}>{name}</h1>}

            <div className="flex flex-col">
              <h2
                className={venueClassName}
              >{`${venue.name}, ${venue.city}`}</h2>
              <h3 className={dateClassName}>{formattedDate}</h3>
            </div>

            {match({
              isLoading: isRegistrationCheckLoading,
              isRegistered: isViewerRegistered,
              isOpen: isRegistrationOpen,
              isPast:
                activeDivision.status !== "closed" ||
                parsedDate.compare(today(getDefaultTimeZone())) < 0,
              price,
              openAt: tournament.registrationOpenAt,
            })
              .with({ isPast: true }, () => null)
              .with({ isLoading: true }, () => (
                <div
                  className={button({
                    radius: "full",
                    isDisabled: true,
                    className: "animate-pulse bg-gray-200 min-w-48",
                  })}
                >
                  &nbsp;
                </div>
              ))
              .with({ isRegistered: true }, () => (
                <div className="flex flex-col items-center gap-2">
                  <span className={tagStyles({ color: "green" })}>
                    <CheckIcon size={12} />
                    You're in
                  </span>
                </div>
              ))
              .with(
                {
                  isOpen: true,
                  price: P.number,
                },
                ({ price }) => (
                  <div className="flex flex-col items-center gap-2">
                    {isWaitlistFull ? (
                      <div
                        className={button({
                          radius: "full",
                          isDisabled: true,
                        })}
                      >
                        Waitlist Full
                      </div>
                    ) : (
                      <Link
                        to="/account/registrations"
                        search={{
                          teams: [
                            {
                              divisionId: activeDivision.id,
                              profileIds: [],
                            },
                          ],
                        }}
                        variant="alt"
                        className={button({
                          color: isDivisionFull ? "secondary" : "primary",
                          radius: "full",
                        })}
                        onClick={() => {
                          posthog.capture("tournament_registration_started", {
                            division_id: activeDivision.id,
                            tournament_id: Number.parseInt(tournamentId, 10),
                            is_waitlist: isDivisionFull,
                            price,
                          });
                        }}
                      >
                        {isDivisionFull
                          ? `Join Waitlist — ${moneyFormatter.format(price)}`
                          : `Register — ${moneyFormatter.format(price)}`}
                      </Link>
                    )}
                    {isDivisionFull && (
                      <span className="text-sm text-gray-500">
                        {activeDivision.waitlistedCount === 1
                          ? "1 team on the waitlist"
                          : `${activeDivision.waitlistedCount} teams on the waitlist`}
                      </span>
                    )}
                  </div>
                ),
              )
              .with(
                {
                  isOpen: false,
                  openAt: P.when(
                    (ts: Date | null): ts is Date =>
                      ts !== null && ts > new Date(),
                  ),
                },
                ({ openAt }) => (
                  <div
                    className={button({
                      radius: "full",
                      isDisabled: true,
                    })}
                  >
                    Registration Opens {shortDateTimeFormatter.format(openAt)}
                  </div>
                ),
              )
              // .exhaustive()
              .otherwise(() => (
                <div
                  className={button({
                    radius: "full",
                    isDisabled: true,
                  })}
                >
                  Registration Closed
                </div>
              ))}

            {viewerMembership && !viewerMembership.tshirtPickedUpAt && (
              <Button
                variant="outline"
                size="sm"
                onPress={() => setTshirtModalOpen(true)}
              >
                <Shirt size={16} />
                T-Shirt Pickup
              </Button>
            )}
          </div>

          <DropdownMenu
            buttonContent={
              <span
                className={subtitle({
                  className: clsx(
                    "w-auto!",
                    activeDivision?.status === "cancelled" && "text-red-500",
                  ),
                })}
              >
                {activeDivision && getTournamentDivisionDisplay(activeDivision)}
                {activeDivision?.status === "cancelled" && " (Cancelled)"}
              </span>
            }
          >
            {tournament.tournamentDivisions.map(
              ({ id, status, ...division }) => (
                <DropdownMenuItemLink
                  key={id}
                  to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                  params={{
                    tournamentId,
                    divisionId: id.toString(),
                  }}
                  search={{ pools: [], courts: [] }}
                  className={clsx(
                    "w-full flex flex-row justify-between",
                    id === activeDivision?.id && "font-semibold",
                    status === "cancelled" && "text-red-400",
                  )}
                >
                  <span>{getTournamentDivisionDisplay(division)}</span>
                  {id === activeDivision?.id && <CheckIcon />}
                </DropdownMenuItemLink>
              ),
            )}
          </DropdownMenu>
        </div>

        <Tabs selectedKey={activeTab} onSelectionChange={onTabChange}>
          <div className="overflow-x-auto">
            <TabList
              aria-label="Tournament Overview"
              className="min-w-max px-6"
            >
              <Tab
                id="info"
                onHoverStart={() => prefetchTab("info")}
                onFocus={() => prefetchTab("info")}
              >
                Information
              </Tab>
              <Tab
                id="teams"
                isDisabled={!canShowTeams}
                onHoverStart={() => prefetchTab("teams")}
                onFocus={() => prefetchTab("teams")}
              >
                Teams
              </Tab>
              <Tab
                id="pools"
                isDisabled={!canShowPools}
                onHoverStart={() => prefetchTab("pools")}
                onFocus={() => prefetchTab("pools")}
              >
                Pools
              </Tab>
              <Tab
                id="games"
                isDisabled={!canShowGames}
                onHoverStart={() => prefetchTab("games")}
                onFocus={() => prefetchTab("games")}
              >
                Games
              </Tab>
              <Tab
                id="playoffs"
                isDisabled={!canShowPlayoffs}
                onHoverStart={() => prefetchTab("playoffs")}
                onFocus={() => prefetchTab("playoffs")}
              >
                Playoffs
              </Tab>
              <Tab
                id="gallery"
                isDisabled={!canShowGallery}
                onHoverStart={() => prefetchTab("gallery")}
                onFocus={() => prefetchTab("gallery")}
              >
                Photos
              </Tab>
            </TabList>
          </div>
        </Tabs>
        <Activity mode={activeTab === "info" ? "visible" : "hidden"}>
          <InformationPanel
            {...tournament}
            isActive={activeTab === "info"}
            divisionId={activeDivision.id}
            divisionDescription={activeDivision.description}
          />
        </Activity>
        <Activity mode={activeTab === "teams" ? "visible" : "hidden"}>
          <TeamsPanel
            {...tournament}
            tournamentDivisionId={activeDivision.id}
            teamSize={activeDivision.teamSize}
            isActive={activeTab === "teams"}
          />
        </Activity>
        <Activity mode={activeTab === "pools" ? "visible" : "hidden"}>
          <PoolsPanel
            {...tournament}
            tournamentDivisionId={activeDivision.id}
            isActive={activeTab === "pools"}
          />
        </Activity>
        <Activity mode={activeTab === "games" ? "visible" : "hidden"}>
          <GamesPanel
            {...tournament}
            tournamentDivisionId={activeDivision.id}
            isActive={activeTab === "games"}
          />
        </Activity>
        <Activity mode={activeTab === "playoffs" ? "visible" : "hidden"}>
          <PlayoffsPanel
            {...tournament}
            tournamentDivisionId={activeDivision.id}
            isActive={activeTab === "playoffs"}
          />
        </Activity>
        <Activity mode={activeTab === "gallery" ? "visible" : "hidden"}>
          <GalleryPanel
            tournamentId={Number.parseInt(tournamentId, 10)}
            tournamentName={`${tournament.venue.name} ${formattedDate}`}
            divisions={tournament.tournamentDivisions}
            isActive={activeTab === "gallery"}
          />
        </Activity>
      </div>
      {viewerMembership && !viewerMembership.tshirtPickedUpAt && (
        <TshirtPickupModal
          isOpen={tshirtModalOpen}
          onOpenChange={setTshirtModalOpen}
          membershipId={viewerMembership.id}
          tshirtSize={viewerMembership.tshirtSize}
        />
      )}
    </DefaultLayout>
  );
}
